// A Custom board's records, changed from inside its app: the kit's messages (widgets/kit/runtime.js) checked here for
// shape and size, then put as entries in one step, so a loop of adds is one Undo and the board syncs them as any
// other change. The app is sealed and sends only plain data; nothing it sends reaches anything but these entries.

import { ENTRY_ID } from "../../../shared/entries";
import { MAX_RECORDS, RecordRefused, cleanRecordFields, mergeFields, recordsOf } from "../../../shared/records";
import type { Entry, ID, Op } from "../../../shared/types";
import { commit } from "../../state/actions";
import { boardEntries, get, toast } from "../../state/store";

/** The most operations one message from the app may carry; more are dropped, as an app's loop that runs away is. */
const MAX_OPS = 2_000;

type RecordOp = { put: string; fields: unknown; merge?: boolean } | { del: string };

/** The app's batch of changes, applied to the board's records as one step. */
export function applyRecordOps(boardId: ID, raw: unknown): void {
  if (!Array.isArray(raw)) return;
  const board = get().boards.find((b) => b.id === boardId);
  if (!board || board.kind !== "custom") return;
  const at = new Map<ID, Entry>();
  for (const e of boardEntries(get(), boardId)) if (e.t === "record") at.set(e.id, e);
  let foot = Math.max(-1, ...recordsOf([...at.values()]).map((e) => e.order));
  const now = Date.now();
  const ops: Op[] = [];
  let made = 0;
  let changed = 0;
  let removed = 0;
  let refusal = "";
  for (const o of raw.slice(0, MAX_OPS) as RecordOp[]) {
    if (!o || typeof o !== "object") continue;
    if ("del" in o) {
      const id = typeof o.del === "string" ? o.del : "";
      if (!at.has(id)) continue;
      at.delete(id);
      ops.push({ op: "entry.del", id });
      removed++;
      continue;
    }
    const id = typeof o.put === "string" && ENTRY_ID.test(o.put) ? o.put : "";
    if (!id) continue;
    const was = at.get(id);
    // an id that belongs to another board's entry is never touched
    if (!was && get().entries[id]) continue;
    try {
      if (o.merge && !was) continue;
      const fields = o.merge ? mergeFields(was!.data, o.fields) : cleanRecordFields(o.fields);
      if (was && JSON.stringify(was.data) === JSON.stringify(fields)) continue;
      if (!was && at.size >= MAX_RECORDS) {
        refusal = `A Custom board holds up to ${MAX_RECORDS.toLocaleString("en-US")} records. Delete some first.`;
        break;
      }
      const entry: Entry = was ? { ...was, data: fields } : { id, boardId, t: "record", parent: null, order: ++foot, data: fields, createdAt: now, updatedAt: now, by: null };
      at.set(id, entry);
      ops.push({ op: "entry.put", entry });
      if (was) changed++;
      else made++;
    } catch (e) {
      if (!(e instanceof RecordRefused)) throw e;
      refusal = e.message;
    }
  }
  if (refusal) toast({ level: "warn", title: "A record was not saved", body: refusal, key: "record-refused" });
  if (!ops.length) return;
  commit(ops, made && !changed && !removed ? (made === 1 ? "Add record" : "Add records") : removed && !made && !changed ? (removed === 1 ? "Delete record" : "Delete records") : changed === 1 && !made && !removed ? "Edit record" : "Edit records");
}
